import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Bell, AlertTriangle, MessageSquare, Truck, PackageX, PackageCheck } from "lucide-react";

const sourceCfg = {
  Cafeteria: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950 dark:text-orange-300",
  Kitchen: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-300",
  Store: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-950 dark:text-blue-300",
};

const eventIcon = {
  short_dispatch: Truck,
  short_issue: PackageX,
  short_receive: AlertTriangle,
  comment: MessageSquare,
};

export default function NotificationsPanel({ admin = false }) {
  const [events, setEvents] = useState([]);
  const [source, setSource] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = () => {
    const params = {};
    if (admin && source !== "All") params.source = source;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/notifications", { params }).then((r) => setEvents(r.data.events)).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [source, dateFrom, dateTo]); // eslint-disable-line

  const warnCount = useMemo(() => events.filter((e) => e.severity === "warning").length, [events]);

  return (
    <div data-testid="notifications-panel">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Bell className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg leading-none">Notifications</h3>
            <p className="text-xs text-muted-foreground mt-1">{warnCount} alerts · {events.length} total</p>
          </div>
        </div>
        <div className="sm:ml-auto flex flex-wrap items-end gap-2">
          {admin && (
            <div className="space-y-1">
              <Label className="text-xs">Source</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="w-36" data-testid="notif-source-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["All", "Cafeteria", "Kitchen", "Store"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" className="w-40" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="notif-date-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" className="w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="notif-date-to" />
          </div>
          {(source !== "All" || dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={() => { setSource("All"); setDateFrom(""); setDateTo(""); }} data-testid="notif-clear-filter">Clear</Button>
          )}
        </div>
      </div>

      <div className="space-y-3" data-testid="notifications-list">
        {events.length === 0 && (
          <Card className="p-10 text-center text-muted-foreground">
            <PackageCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No short-supply or short-receipt notifications. Everything matched up.
          </Card>
        )}
        {events.map((e) => {
          const Icon = eventIcon[e.event] || Bell;
          const warn = e.severity === "warning";
          return (
            <Card key={e.id} className={`p-4 fade-up border-l-4 ${warn ? "border-l-amber-500" : "border-l-blue-400"} border-orange-100 dark:border-slate-800`} data-testid="notification-card">
              <div className="flex items-start gap-3">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${warn ? "bg-amber-100 dark:bg-amber-950" : "bg-blue-100 dark:bg-blue-950"}`}>
                  <Icon className={`h-4 w-4 ${warn ? "text-amber-700 dark:text-amber-400" : "text-blue-700 dark:text-blue-400"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm">{e.title}</p>
                    <Badge variant="outline" className={`${sourceCfg[e.source] || ""} text-[10px]`}>{e.source}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{e.message}</p>
                  {e.comment && <p className="text-sm italic mt-1 rounded-md bg-secondary/60 px-2 py-1">“{e.comment}”</p>}
                  <p className="text-[11px] text-muted-foreground mt-1.5 font-mono">
                    {e.location ? `${e.location} · ` : ""}{e.by ? `${e.by} · ` : ""}
                    {e.created_at ? new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
